import React, { useState } from 'react';
import {
  Drawer,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Tag,
  List,
  Button,
  message,
  Badge,
  Divider
} from 'antd';
import { useQuery } from 'react-query';
import { debtService, Debt } from '../../services/debtService';
import DebtPaymentModal from '../../components/debts/DebtPaymentModal';
import { calculateGroupedDebtStatus, getDebtStatusColor, getDebtStatusText } from '../../utils/debtStatusUtils';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Customer } from '../../services/customerService';

const { Title, Text } = Typography;

const statusIcon = {
  unpaid: <ExclamationCircleOutlined />,
  partial: <ClockCircleOutlined />,
  paid: <CheckCircleOutlined />,
};

interface Props {
  debt: Debt;
  onClose: () => void;
  onPaid: () => void;
  customers?: Customer[];
  allDebts?: Debt[];
}

const DebtDetailDrawer: React.FC<Props> = ({ debt, onClose, onPaid, customers = [], allDebts }) => {
  const { data, refetch } = useQuery(['debt-detail', debt.id], () => debtService.getDebtDetail(debt.id));
  const [payModal, setPayModal] = useState(false);

  // Dùng allDebts truyền vào nếu có, nếu không thì fetch
  const { data: fetchedDebts = [] } = useQuery(
    ['customer-debts', debt.customer_id],
    () => debtService.getDebts(String(debt.customer_id)),
    { enabled: !!debt.customer_id && !allDebts }
  );
  const debtsToShow = allDebts && allDebts.length > 0 ? allDebts : fetchedDebts;

  // Tổng hợp số liệu toàn bộ công nợ của khách hàng
  const stats = React.useMemo(() => {
    if (!debtsToShow || debtsToShow.length === 0) return { totalDebt: 0, totalPaid: 0, remain: 0, status: 'unpaid' as 'unpaid' };
    
    const statusInfo = calculateGroupedDebtStatus(debtsToShow);
    return statusInfo;
  }, [debtsToShow]);

  // Tổng hợp tất cả payments của khách hàng
  const allPayments = React.useMemo(() => {
    if (!debtsToShow || debtsToShow.length === 0) return [];
    // Chỉ lấy payments nếu có (do DebtsPage truyền sang)
    return debtsToShow.flatMap((d: any) => Array.isArray(d.payments) ? d.payments : []).filter(Boolean);
  }, [debtsToShow]);

  // Gom lịch sử giao dịch (ghi nợ + thanh toán) thành 1 bảng
  const transactions = React.useMemo(() => {
    if (!debtsToShow || debtsToShow.length === 0) return [];
    // Giao dịch ghi nợ
    const debtTxs = debtsToShow.map((d) => ({
      type: 'debt',
      date: d.created_at,
      amount: d.debt_amount,
      note: (d as any).note || '',
      id: d.id,
      voucher: (d as any).voucher_number || (d as any).stock_out_voucher_id || d.id,
    }));
    // Giao dịch thanh toán
    const paymentTxs = debtsToShow.flatMap((d) =>
      Array.isArray((d as any).payments)
        ? (d as any).payments.map((p: any) => ({
            type: 'payment',
            date: p.payment_date,
            amount: p.amount,
            note: p.note || '',
            id: p.id,
            voucher: (p as any).voucher_number || (d as any).voucher_number || (d as any).stock_out_voucher_id || d.id,
          }))
        : []
    );
    // Gộp và sort theo ngày
    const allTxs = [...debtTxs, ...paymentTxs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    // Tính số dư sau giao dịch
    let balance = 0;
    return allTxs.map((tx) => {
      if (tx.type === 'debt') {
        balance += tx.amount;
        return { ...tx, in: 0, out: tx.amount, balance: balance };
      } else {
        balance -= tx.amount;
        return { ...tx, in: tx.amount, out: 0, balance: balance };
      }
    });
  }, [debtsToShow]);

  if (!data) return null;
  
  // Sử dụng thông tin khách hàng từ API response nếu có, nếu không thì tìm trong customers
  const customerFromResponse = data.debt.customer_name ? {
    id: data.debt.customer_id,
    name: data.debt.customer_name,
    code: data.debt.customer_code,
    phone: data.debt.customer_phone,
    address: data.debt.customer_address
  } : null;
  const customer = customerFromResponse || customers.find(c => c.id === data.debt.customer_id);

  return (
    <Drawer
      open
      onClose={onClose}
      width="87%"
      title={<Title level={4} style={{ margin: 0 }}>Chi tiết công nợ</Title>}
      bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
      destroyOnClose
    >
      <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
        {/* Left: Thông tin công nợ + lịch sử thanh toán */}
        <div style={{ flex: 1, padding: 24, borderRight: '1px solid #f0f0f0', minWidth: 0, overflow: 'auto' }}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Số nợ"
                  value={stats.totalDebt}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#faad14' }}
                  formatter={v => v?.toLocaleString('vi-VN')}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Đã trả"
                  value={stats.totalPaid}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                  formatter={v => v?.toLocaleString('vi-VN')}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Còn lại"
                  value={stats.remain}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                  formatter={v => v?.toLocaleString('vi-VN')}
                />
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Trạng thái</div>
                  <Tag 
                    color={getDebtStatusColor(stats.status)} 
                    icon={statusIcon[stats.status]}
                    style={{ margin: 0, fontSize: 12 }}
                  >
                    {getDebtStatusText(stats.status)}
                  </Tag>
                </div>
              </Col>
            </Row>
          </Card>
          {/* Bảng lịch sử giao dịch tổng hợp */}
          <Card size="small" style={{ marginBottom: 16, minHeight: 200 }}>
            <Title level={5} style={{ marginBottom: 12 }}>Lịch sử giao dịch công nợ</Title>
            {transactions.length > 0 ? (
              <List
                dataSource={transactions}
                renderItem={(tx, idx) => (
                  <List.Item style={{ display: 'flex', alignItems: 'center', padding: '6px 0' }}>
                    <span style={{ flex: 0.5, textAlign: 'center' }}>{idx + 1}</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>{tx.date?.slice(0, 10)}</span>
                    <span style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
                      {tx.type === 'debt'
                        ? `Ghi nợ${tx.voucher ? ' - ' + tx.voucher : ''}`
                        : `Thanh toán${tx.voucher ? ' - ' + tx.voucher : ''}`}
                    </span>
                    <span style={{ flex: 1, textAlign: 'center', color: '#52c41a', fontWeight: 600 }}>{tx.in > 0 ? `+${tx.in.toLocaleString('vi-VN')}` : ''}</span>
                    <span style={{ flex: 1, textAlign: 'center', color: '#ff4d4f', fontWeight: 600 }}>{tx.out > 0 ? `-${tx.out.toLocaleString('vi-VN')}` : ''}</span>
                    <span style={{ flex: 2, textAlign: 'center', color: '#888' }}>{tx.note}</span>
                    <span style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>{tx.balance.toLocaleString('vi-VN')}</span>
                  </List.Item>
                )}
                header={
                  <div style={{ display: 'flex', fontWeight: 700, color: '#888', background: '#fafafa', padding: '8px 0' }}>
                    <span style={{ flex: 0.5, textAlign: 'center' }}>STT</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Ngày</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Loại</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Tiền vào</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Tiền ra</span>
                    <span style={{ flex: 2, textAlign: 'center' }}>Ghi chú</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Số dư</span>
                  </div>
                }
              />
            ) : (
              <div style={{ textAlign: 'center', color: '#999' }}>Không có giao dịch</div>
            )}
          </Card>
        </div>
        {/* Right: Thông tin khách hàng, nút thanh toán, ghi chú */}
        <div style={{ width: 350, padding: 24, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto', background: '#fafcff' }}>
          <Title level={5} style={{ marginBottom: 16 }}>Thông tin khách hàng</Title>
          <div style={{ marginBottom: 12 }}>
            <Text strong>Tên khách hàng:</Text> {customer ? customer.name : data.debt.customer_id}
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong>Mã KH:</Text> {customer ? customer.code : 'N/A'}
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong>SĐT:</Text> {customer?.phone || 'N/A'}
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong>Địa chỉ:</Text> {customer?.address || 'N/A'}
          </div>
          <Divider style={{ margin: '16px 0' }} />
          <Button
            type="primary"
            block
            size="large"
            style={{ marginBottom: 16 }}
            onClick={() => setPayModal(true)}
            disabled={debtsToShow.filter(d => d.debt_amount > d.paid_amount).length === 0}
          >
            Thanh toán công nợ
          </Button>
          {payModal && (
            <DebtPaymentModal
              debt={data.debt}
              debts={debtsToShow.filter(d => d.debt_amount > d.paid_amount)}
              onClose={() => setPayModal(false)}
              onPaid={() => {
                setPayModal(false);
                refetch();
                onPaid();
                message.success('Thanh toán thành công!');
              }}
            />
          )}
          <Divider style={{ margin: '16px 0' }} />
          <Title level={5} style={{ marginBottom: 8, fontSize: 16 }}>Ghi chú</Title>
          <div style={{ minHeight: 60, color: '#666', background: '#fff', borderRadius: 4, padding: 12, border: '1px solid #f0f0f0' }}>
            {'Không có ghi chú'}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default DebtDetailDrawer; 